import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { z } from "zod";
import { useRegister } from "@empite/session";
import { applyApiFieldErrors, useZodForm } from "@empite/app-kit";
import { AuthShell } from "../ui/auth-shell";
import { Button } from "../ui/button";
import { Field } from "../ui/field";
import { Notice } from "../ui/notice";
import { PasswordStrength } from "../ui/password-strength";
import { UiLink } from "../ui/router";
import { errorMessage } from "./guards";

const schema = z.object({
  email: z.string().min(1, "Enter your email address.").email("That is not an email address."),
  // The platform's own rule is 12 characters; the same number is checked here
  // only so the browser can say so without a round trip. The server remains
  // the authority, and its violations are pinned back onto these fields.
  password: z.string().min(12, "Use at least 12 characters."),
  phone_number: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/, "Enter the number in international form, like +61400000000."),
  display_name: z.string().optional(),
});

type Values = z.infer<typeof schema>;

export function Register() {
  const navigate = useNavigate();
  const register = useRegister();
  const [formError, setFormError] = useState<unknown>(null);
  const [revealed, setRevealed] = useState(false);
  const form = useZodForm<Values>(schema, {
    defaultValues: { email: "", password: "", phone_number: "", display_name: "" },
  });
  // The strength meter reads the live value, so this one field is watched
  // rather than left to react-hook-form's uncontrolled default.
  const password = form.watch("password");

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      const status = await register.mutateAsync({
        email: values.email,
        password: values.password,
        phone_number: values.phone_number,
        display_name: values.display_name || undefined,
      });
      // Registration does NOT sign anyone in — there is no session until the
      // phone is verified and the customer signs in — so the user_id travels
      // to the OTP screen in the URL rather than being read off a session.
      await navigate({ to: "/verify-phone", search: { user_id: status.user_id, next: undefined } });
    } catch (err) {
      if (!applyApiFieldErrors(form.setError, err, ["email", "password", "phone_number", "display_name"])) {
        setFormError(err);
      }
    }
  });

  return (
    <AuthShell
      title="Create an account"
      subtitle="One account, every application in the suite."
      panel={{
        headline: "Start with one account. Add applications as you need them.",
        sub: "Each application you use keeps its own wallet and its own usage record.",
        bullets: [
          "Credit is added per application and spent only there",
          "Every priced request is listed with what it cost",
          "New device installations wait for your approval",
        ],
      }}
      footer={
        <p className="text-center text-sm text-ink-3">
          Already have an account?{" "}
          <UiLink
            href="/sign-in"
            search={{ next: undefined, client_id: undefined }}
            className="font-semibold text-brand hover:text-brand-strong"
          >
            Sign in
          </UiLink>
          .
        </p>
      }
    >
      {/*
        The design's create-account form also carries a terms-of-service
        checkbox that gates submission. There is no consent to record on this
        side — the register call takes no such field and the platform stores
        none — so a checkbox here would be a second gate in front of a real
        mutation that nothing downstream reads. It is dropped rather than faked.
      */}
      <form onSubmit={submit} noValidate className="mt-7 space-y-4">
        {formError ? (
          <Notice
            tone="danger"
            title="Registration failed"
            icon={<AlertCircle className="size-4" aria-hidden />}
          >
            {errorMessage(formError)}
          </Notice>
        ) : null}

        <Field
          label="Email"
          type="email"
          autoComplete="username"
          placeholder="you@company.com"
          error={form.formState.errors.email?.message}
          {...form.register("email")}
        />

        <div>
          <Field
            label="Password"
            // Same reveal as sign-in: the input's type is swapped in place so
            // the browser's password manager keeps tracking one element.
            type={revealed ? "text" : "password"}
            autoComplete="new-password"
            error={form.formState.errors.password?.message}
            suffix={
              <button
                type="button"
                onClick={() => setRevealed((shown) => !shown)}
                aria-label={revealed ? "Hide password" : "Show password"}
                aria-pressed={revealed}
                className="grid size-7 place-items-center rounded-md text-ink-4 transition-colors hover:text-ink-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                {revealed ? (
                  <EyeOff className="size-4" aria-hidden />
                ) : (
                  <Eye className="size-4" aria-hidden />
                )}
              </button>
            }
            {...form.register("password")}
          />
          <PasswordStrength value={password ?? ""} />
        </div>

        <Field
          label="Mobile number"
          type="tel"
          autoComplete="tel"
          placeholder="+61400000000"
          hint="International form, including the country code."
          error={form.formState.errors.phone_number?.message}
          {...form.register("phone_number")}
        />

        <Field
          label="Display name"
          autoComplete="name"
          placeholder="Demo Customer"
          hint="Optional."
          error={form.formState.errors.display_name?.message}
          {...form.register("display_name")}
        />

        <Button type="submit" size="lg" className="w-full" disabled={register.isPending}>
          {register.isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Creating your account…
            </>
          ) : (
            "Create an account"
          )}
        </Button>

        <p className="text-center text-2xs text-ink-4">
          Your phone number is verified once and used to protect every application in the
          suite.
        </p>
      </form>
    </AuthShell>
  );
}
